import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Switch, message, Popconfirm, Space, Tag, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, LinkOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Option } = Select;

const ReferralCampaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getReferralCampaigns();
      setCampaigns(res.data || []);
    } catch (error) {
      message.error('获取活动列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCampaign(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingCampaign(record);
    form.setFieldsValue({
      name: record.name,
      status: record.status,
      bonus_amount: record.bonus_amount,
      condition_min: record.condition_min,
      max_recipients: record.max_recipients,
      allow_merchant: record.allow_merchant || false,
      merchant_wallet_type: record.merchant_wallet_type || 'balance',
      description: record.description,
      start_time: record.start_time ? record.start_time.split('T')[0] : null,
      end_time: record.end_time ? record.end_time.split('T')[0] : null,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await adminAPI.deleteReferralCampaign(id);
      message.success('删除成功');
      fetchCampaigns();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        start_time: values.start_time ? new Date(values.start_time).toISOString() : null,
        end_time: values.end_time ? new Date(values.end_time).toISOString() : null,
      };

      if (editingCampaign) {
        await adminAPI.updateReferralCampaign(editingCampaign.id, data);
        message.success('更新成功');
      } else {
        await adminAPI.createReferralCampaign(data);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchCampaigns();
    } catch (error) {
      message.error(editingCampaign ? '更新失败' : '创建失败');
    }
  };

  // 复制活动链接
  const copyCampaignLink = (campaignCode) => {
    const baseUrl = window.location.origin.replace(':3001', ':3000'); // 替换端口为用户端
    const link = `${baseUrl}/c/${campaignCode}`;
    // 使用textarea作为fallback，解决HTTP环境下navigator.clipboard不可用的问题
    const textarea = document.createElement('textarea');
    textarea.value = link;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      message.success('活动链接已复制');
    } catch (err) {
      message.error('复制失败，请手动复制');
    }
    document.body.removeChild(textarea);
  };

  // 获取活动链接
  const getCampaignLink = (campaignCode) => {
    const baseUrl = window.location.origin.replace(':3001', ':3000');
    return `${baseUrl}/c/${campaignCode}`;
  };

  // 根据时间判断活动是否真正进行中
  const getRealStatus = (record) => {
    const now = new Date();
    // 如果管理员设置状态为关闭，直接返回关闭
    if (record.status !== 'active') {
      return { text: '已关闭', color: 'red' };
    }
    // 检查时间范围
    if (record.start_time && new Date(record.start_time) > now) {
      return { text: '未开始', color: 'blue' };
    }
    if (record.end_time && new Date(record.end_time) < now) {
      return { text: '已过期', color: 'orange' };
    }
    // 检查是否达到最大奖励人数
    if (record.max_recipients > 0 && record.current_count >= record.max_recipients) {
      return { text: '已满员', color: 'purple' };
    }
    return { text: '进行中', color: 'green' };
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '活动名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
    },
    {
      title: '活动链接',
      dataIndex: 'campaign_code',
      key: 'campaign_code',
      width: 180,
      render: (code, record) => {
        const link = getCampaignLink(code);
        return (
          <Space>
            <Tooltip title={link}>
              <Tag icon={<LinkOutlined />} color="blue" style={{ cursor: 'pointer' }}>
                /c/{code}
              </Tag>
            </Tooltip>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyCampaignLink(code)}
            />
          </Space>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (_, record) => {
        const status = getRealStatus(record);
        return (
          <Tag color={status.color}>
            {status.text}
          </Tag>
        );
      },
    },
    {
      title: '奖励金额',
      dataIndex: 'bonus_amount',
      key: 'bonus_amount',
      width: 100,
      render: (amount) => `$${parseFloat(amount).toFixed(2)}`,
    },
    {
      title: '最低充值',
      dataIndex: 'condition_min',
      key: 'condition_min',
      width: 100,
      render: (val) => val > 0 ? `$${parseFloat(val).toFixed(2)}` : '无要求',
    },
    {
      title: '已奖励/最大',
      key: 'recipients',
      width: 110,
      render: (_, record) => `${record.current_count || 0} / ${record.max_recipients || '无限制'}`,
    },
    {
      title: '商户参与',
      dataIndex: 'allow_merchant',
      key: 'allow_merchant',
      width: 140,
      render: (val, record) => {
        if (!val) {
          return <Tag color="default">不允许</Tag>;
        }
        const walletText = record.merchant_wallet_type === 'withdrawable' ? '可提现' : '消费账户';
        return (
          <Tag color="green">
            允许 ({walletText})
          </Tag>
        );
      },
    },
    {
      title: '活动时间',
      key: 'time',
      width: 200,
      render: (_, record) => {
        const start = record.start_time ? record.start_time.split('T')[0] : '不限';
        const end = record.end_time ? record.end_time.split('T')[0] : '不限';
        return `${start} ~ ${end}`;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0} wrap>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => copyCampaignLink(record.campaign_code)}
          >
            复制链接
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此活动？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          创建活动
        </Button>
        <span style={{ color: '#888', fontSize: 12 }}>
          创建活动后，复制活动链接投放至不同渠道进行推广
        </span>
      </div>

      <Table
        columns={columns}
        dataSource={campaigns}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{ showSizeChanger: true, showQuickJumper: true }}
      />

      <Modal
        title={editingCampaign ? '编辑活动' : '创建活动'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnHidden
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="活动名称"
            rules={[{ required: true, message: '请输入活动名称' }]}
          >
            <Input placeholder="例如：渠道A专属活动" />
          </Form.Item>

          <Form.Item name="status" label="状态" initialValue="inactive">
            <Select>
              <Option value="active">进行中</Option>
              <Option value="inactive">已关闭</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="bonus_amount"
            label="奖励金额 ($)"
            rules={[{ required: true, message: '请输入奖励金额' }]}
            extra="邀请人获得的奖励金额，进入消费账户"
          >
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="condition_min" label="最低充值要求 ($)" extra="被邀请人需充值达到此金额，邀请人才可获得奖励，0表示无要求">
            <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="max_recipients" label="最大奖励人数" extra="最多奖励多少人，0表示无限制">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="allow_merchant"
            label="允许商户参与"
            valuePropName="checked"
            initialValue={false}
            extra="开启后，商户邀请用户可获得活动奖励；关闭则商户不能获得此活动的奖励"
          >
            <Switch checkedChildren="允许" unCheckedChildren="禁止" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.allow_merchant !== currentValues.allow_merchant}
          >
            {({ getFieldValue }) =>
              getFieldValue('allow_merchant') ? (
                <Form.Item
                  name="merchant_wallet_type"
                  label="商户奖励钱包"
                  initialValue="balance"
                  extra="选择商户活动奖励进入哪个钱包"
                >
                  <Select>
                    <Option value="balance">消费账户（和普通用户一样）</Option>
                    <Option value="withdrawable">可提现账户（商户可申请提现）</Option>
                  </Select>
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item name="start_time" label="开始时间">
            <Input type="date" />
          </Form.Item>

          <Form.Item name="end_time" label="结束时间">
            <Input type="date" />
          </Form.Item>

          <Form.Item name="description" label="活动说明">
            <Input.TextArea rows={3} placeholder="活动规则说明..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReferralCampaigns;
